import { useEffect, useRef } from "react";

import { Stack } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { DateTime } from "luxon";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { useShipmentETAs, useShipmentQuotes, useShipmentRoute } from "hooks";
import {
  getBookedQuoteFromQuotes,
  ISOStringToLocalReadableTimestamp,
} from "utils";

import {
  buildShipmentStatusListFromRoutePointsETAs,
  buildShipmentStatusListFromShipmentLifecycle,
  getBookedStatusLabel,
} from "./utils";

import type { ShipmentStatus } from "./types";

interface Props {
  shipment: CondensedShipment;
}

interface RowProps {
  status: ShipmentStatus;
}

function ShipmentStatusRow(props: RowProps) {
  const { status } = props;
  const { type, createdAt, createdBy, notes, routePointNumber, timestamp } =
    status;
  return (
    <TableRow>
      <TableCell>{type}</TableCell>
      <TableCell>
        {createdAt ? ISOStringToLocalReadableTimestamp(createdAt) : "-"}
      </TableCell>
      <TableCell>
        {timestamp ? ISOStringToLocalReadableTimestamp(timestamp) : "NA"}
      </TableCell>
      <TableCell>{notes}</TableCell>
      <TableCell>{routePointNumber ?? ""}</TableCell>
      <TableCell>{createdBy}</TableCell>
    </TableRow>
  );
}

export function ShipmentStatusHistoryTable(props: Props) {
  const { shipment } = props;
  const {
    etas,
    loading: etasLoading,
    error: etaFetchError,
    refetchETAs,
  } = useShipmentETAs(shipment.info.id);
  const {
    route,
    loading: routeLoading,
    error: routeFetchError,
    refetchRoute,
  } = useShipmentRoute(shipment.info.route);
  const {
    quotes,
    isLoading: quotesLoading,
    error: quotesFetchError,
  } = useShipmentQuotes(shipment.info.id);

  const shipmentRef = useRef<undefined | CondensedShipment>();

  useEffect(() => {
    if (
      shipmentRef.current != null &&
      shipmentRef.current.version !== shipment.version
    ) {
      refetchETAs();
      refetchRoute();
    }
    shipmentRef.current = shipment;
  }, [shipment, refetchETAs, refetchRoute]);

  const shipmentStatusHistory = buildShipmentStatusListFromShipmentLifecycle(
    shipment,
    route,
  );
  const shipmentStatusHistoryETAs = buildShipmentStatusListFromRoutePointsETAs(
    etas ?? [],
  );

  const combinedStatus = shipmentStatusHistory.concat(
    shipmentStatusHistoryETAs,
  );
  combinedStatus.sort((a, b) => {
    const dateA = DateTime.fromISO(a.createdAt).toMillis();
    const dateB = DateTime.fromISO(b.createdAt).toMillis();
    return dateB - dateA;
  });

  const bookedQuote = getBookedQuoteFromQuotes(quotes);
  if (bookedQuote != null) {
    const bookedQuoteStatus: ShipmentStatus = {
      type: getBookedStatusLabel(bookedQuote),
      createdAt: bookedQuote.created_at,
      createdBy: `${bookedQuote.action_user_author?.first_name ?? "-"} ${bookedQuote.action_user_author?.last_name ?? "-"}`,
      notes: bookedQuote.notes ?? "",
      routePointNumber: null,
      timestamp: shipment.info.booked_at ?? null,
    };
    combinedStatus.push(bookedQuoteStatus);
  }

  return (
    <Stack direction="column" spacing={1}>
      <LoadingWrappedNode
        loading={etasLoading || routeLoading || quotesLoading}
      >
        <Stack mt="1rem">
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Provided Timestamp</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Route Point</TableCell>
                  <TableCell>Created By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {combinedStatus.map((status) => (
                  <ShipmentStatusRow key={status.createdAt} status={status} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </LoadingWrappedNode>
      <ErrorTypography error={etaFetchError} />
      <ErrorTypography error={routeFetchError} />
      <ErrorTypography error={quotesFetchError} />
    </Stack>
  );
}
